import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pulse, ArrowRight, CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import { loadNotificationsHealth, loadPushCoverage, loadPublicSiteHealth } from "@/lib/systemHealth";
import { loadModerationDigest } from "@/lib/photoModeration";
import { ROLES } from "@/lib/roles";

/**
 * Admin overview · one-line system health widget.
 *
 * Distills the same signals as `/admin/system` into a single ribbon:
 *   {derived-OK count} of {total derived items} healthy ·
 *   {pending-manual count} pending setup ·
 *   {soft warning} if any
 *
 * Fully read-only. Loads in parallel with the rest of the dashboard
 * so it never blocks the primary content. When a sub-load throws (e.g.
 * the user's RLS scope just changed), the widget falls back to a calm
 * "couldn't check" state rather than disappearing.
 */
export default function AdminSystemWidget() {
  const [state, setState] = useState({ loading: true, summary: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [notifications, push, site, moderation] = await Promise.all([
          loadNotificationsHealth({ days: 7 }).catch(() => null),
          loadPushCoverage().catch(() => null),
          loadPublicSiteHealth().catch(() => null),
          loadModerationDigest({ days: 7 }).catch(() => null),
        ]);
        if (!alive) return;
        setState({
          loading: false,
          summary: deriveSummary({ notifications, push, site, moderation }),
        });
      } catch {
        if (alive) setState({ loading: false, summary: null });
      }
    })();
    return () => { alive = false; };
  }, []);

  const { loading, summary } = state;

  return (
    <Link
      to="/admin/system"
      data-testid="admin-system-widget"
      data-state={summary?.tone || (loading ? "loading" : "muted")}
      className={`group flex items-center justify-between gap-3 rounded-2xl border p-4 transition-all hover:-translate-y-[2px] hover:shadow-md ${
        summary?.tone === "ok"
          ? "border-primary/30 bg-primary/5"
          : summary?.tone === "warn"
            ? "border-[#EFBA57]/30 bg-[#EFBA57]/5"
            : summary?.tone === "fail"
              ? "border-[#C6851E]/30 bg-[#C6851E]/5"
              : "border-neutral-200 bg-white"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl ${
            summary?.tone === "ok"
              ? "bg-white text-primary-hover"
              : summary?.tone === "warn" || summary?.tone === "fail"
                ? "bg-white text-[#C6851E]"
                : "bg-primary/10 text-primary-hover"
          }`}
        >
          {loading ? (
            <CircleNotch size={16} weight="bold" className="animate-spin" />
          ) : summary?.tone === "ok" ? (
            <CheckCircle size={16} weight="duotone" />
          ) : summary?.tone === "warn" || summary?.tone === "fail" ? (
            <WarningCircle size={16} weight="duotone" />
          ) : (
            <Pulse size={16} weight="duotone" />
          )}
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
            System health
          </p>
          <p
            data-testid="admin-system-widget-summary"
            className="mt-0.5 truncate font-heading text-sm font-semibold text-neutral-900"
          >
            {loading ? "Checking…" : summary?.headline || "Couldn't check right now"}
          </p>
          {!loading && summary?.detail && (
            <p
              data-testid="admin-system-widget-detail"
              className="mt-0.5 truncate text-[11px] text-neutral-500"
            >
              {summary.detail}
            </p>
          )}
        </div>
      </div>
      <ArrowRight
        size={14}
        weight="bold"
        className="flex-shrink-0 text-neutral-400 transition group-hover:translate-x-1 group-hover:text-primary-hover"
      />
    </Link>
  );
}

/**
 * Boil four loaded panels down to a single ribbon. Mirrors the
 * derived-checklist logic on /admin/system so the widget never tells
 * a different story from the page it links to.
 */
function deriveSummary({ notifications, push, site, moderation }) {
  // Mirrors the Production Checklist on /admin/system.
  const items = [];
  // Email channel
  const sent = notifications?.by_status?.sent || 0;
  const failed = notifications?.by_status?.failed || 0;
  items.push({
    state: sent > 0 ? "ok" : failed > 0 ? "fail" : "pending",
    label: "Email",
  });
  // Push activation — at least one device subscribed across all roles.
  const pushActive =
    (push?.[ROLES.PARENT]?.opted_in || 0) +
    (push?.[ROLES.TEACHER]?.opted_in || 0) +
    (push?.[ROLES.ADMIN]?.opted_in || 0) > 0;
  items.push({ state: pushActive ? "ok" : "pending", label: "Push" });
  // Public site
  const pagesLive = site?.pages?.published || 0;
  items.push({ state: pagesLive > 0 ? "ok" : "pending", label: "Public site" });
  // Moderation backlog
  const pending = moderation?.pending_now || 0;
  items.push({ state: pending === 0 ? "ok" : "warn", label: "Moderation" });

  const okCount = items.filter((i) => i.state === "ok").length;
  const failures = items.filter((i) => i.state === "fail");
  const warnings = items.filter((i) => i.state === "warn");
  const pendings = items.filter((i) => i.state === "pending");

  let tone = "muted";
  if (failures.length) tone = "fail";
  else if (warnings.length) tone = "warn";
  else if (okCount === items.length) tone = "ok";

  let headline;
  if (tone === "ok") {
    headline = `All ${items.length} channels healthy`;
  } else if (tone === "fail") {
    headline =
      failures.length === 1
        ? "1 channel needs attention"
        : `${failures.length} channels need attention`;
  } else if (tone === "warn") {
    headline = `${okCount} of ${items.length} healthy · ${warnings.map((i) => i.label).join(", ")} needs review`;
  } else {
    headline = `${okCount} of ${items.length} channels active`;
  }

  // Detail: list the pending labels first so admins know what's still
  // left to flip on. Truncated to keep this on one line on a phone.
  const pendingLabels = pendings.map((i) => i.label);
  const detail =
    pendingLabels.length === 0
      ? "Tap to see the full production checklist"
      : `Pending: ${pendingLabels.join(", ")}`;

  return { tone, headline, detail };
}
